/* ──────────────────────────────────────────────────────────────────────────
   Subscription / shop policy reader modal.

   Extracted from snippets/subscription-policy-modal.liquid on 2026-09-14: the
   same code used to ship as a ~7KB inline <script> in every page's HTML, where
   it executed synchronously during parse. Nothing in it runs before a click —
   every entry point is a delegated listener on `document` — so moving it to a
   deferred external file costs no behaviour and buys three things: ~7KB off
   every HTML response, no parser pause, and one browser-cached copy across
   page views instead of a re-download per navigation.

   Contains no Liquid. The policy URL comes from the modal element's
   data-policy-url attribute, which the snippet still renders.
   ────────────────────────────────────────────────────────────────────────── */
(function () {
  var modal = document.getElementById('mm-subpolicy-modal');
  if (!modal || modal.dataset.wired) return;
  modal.dataset.wired = '1';

  var panel = modal.querySelector('.mm-spm__panel');
  var body = modal.querySelector('[data-spm-body]');
  var titleEl = modal.querySelector('#mm-spm-title');
  var fullEl = modal.querySelector('[data-spm-full]');
  var DEFAULT_URL = modal.getAttribute('data-policy-url');
  var cache = {};      // url → extracted HTML, so reopening never refetches
  var reqId = 0;       // guards against a slow fetch landing after a newer one
  var prevOverflow = '';
  var SPINNER = '<div class="mm-spm__spinner" role="status" aria-label="Loading policy"></div>';

  /* Both page shapes on this store: Shopify's own /policies/* pages render
     .shopify-policy__body, theme pages render .mm-prose / .mm-page__content. */
  function extract(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var content = doc.querySelector('.shopify-policy__body') ||
                  doc.querySelector('.mm-prose') ||
                  doc.querySelector('.mm-page__content');
    if (!content) return null;
    var heading = doc.querySelector('.shopify-policy__title h1') ||
                  doc.querySelector('.mm-page__h') ||
                  doc.querySelector('h1');
    return {
      html: content.innerHTML,
      title: heading ? heading.textContent.replace(/\s+/g, ' ').trim() : ''
    };
  }

  function load(url, title) {
    var mine = ++reqId;
    // Whatever the trigger told us wins; the page's own <h1> is the fallback.
    if (titleEl) titleEl.textContent = title || 'Policy';
    if (fullEl) fullEl.setAttribute('href', url);

    if (cache[url]) {
      body.innerHTML = cache[url].html;
      if (titleEl && !title && cache[url].title) titleEl.textContent = cache[url].title;
      return;
    }

    body.innerHTML = SPINNER;
    fetch(url, { headers: { 'X-Requested-With': 'fetch' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (html) {
        var found = extract(html);
        if (!found) throw 'no-content';
        cache[url] = found;
        if (mine !== reqId) return; // a different policy was opened meanwhile
        body.innerHTML = found.html;
        if (titleEl && !title && found.title) titleEl.textContent = found.title;
      })
      .catch(function () {
        if (mine !== reqId) return;
        body.innerHTML = '<p class="mm-spm__loading">Couldn’t load the policy here. ' +
          '<a href="' + url + '" target="_blank" rel="noopener">Open it in a new tab ↗</a></p>';
      });
  }

  /* Page lock. Prefer the theme's shared owner-keyed lock (pdp-trimmer.js,
     window.lockBodyScroll) so opening this reader over the cart drawer and
     closing either one in either order leaves the page in the right state.
     The prevOverflow fallback is for layouts that don't load pdp-trimmer.js;
     on its own it could restore 'hidden' after the drawer had already
     unlocked (Escape closed both at once) and strand the page unscrollable. */
  function lockPage() {
    if (typeof window.lockBodyScroll === 'function') { window.lockBodyScroll('policy-modal'); return; }
    prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  function unlockPage() {
    if (typeof window.unlockBodyScroll === 'function') { window.unlockBodyScroll('policy-modal'); return; }
    document.body.style.overflow = prevOverflow === 'hidden' ? '' : prevOverflow;
  }

  function open(url, title) {
    load(url || DEFAULT_URL, title);
    // Re-append to <body> so we're the last top-level element — the cart
    // drawer also uses max-int z-index, so DOM order is what breaks the tie.
    if (modal.parentNode !== document.body || document.body.lastElementChild !== modal) {
      document.body.appendChild(modal);
    }
    modal.hidden = false;
    lockPage();
    void modal.offsetWidth; // reflow so the transition runs
    modal.classList.add('is-open');
  }

  function close() {
    if (modal.hidden) return;
    modal.classList.remove('is-open');
    unlockPage();
    var done = function () { modal.hidden = true; };
    var fired = false;
    var onEnd = function () {
      if (fired) return;
      fired = true;
      panel.removeEventListener('transitionend', onEnd);
      done();
    };
    panel.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, 320);
  }

  // Capture phase + stopPropagation so a policy link inside the Kaching bar
  // (or a cart row) opens the reader without the host's own click handler
  // (bar select, row navigation) also firing.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    // Modifier-clicks and middle-clicks belong to the browser — let someone
    // deliberately opening the policy in a new tab do exactly that.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    // The reader's own "View full page ↗" footer is a /policies/* link, so
    // without this bail the interceptor below would swallow it and re-open
    // the same policy in the modal. The escape hatch must actually escape
    // (its target=_blank keeps the PDP alive behind the new tab).
    if (e.target.closest('[data-spm-full]')) return;

    var trigger = e.target.closest('[data-mm-policy]');
    if (trigger) {
      var url = trigger.getAttribute('data-mm-policy') ||
                trigger.getAttribute('href');
      if (!url) return;
      e.preventDefault();
      e.stopPropagation();
      open(url, trigger.getAttribute('data-mm-policy-title') || '');
      return;
    }

    // Subscription-policy links (Kaching card footer, How-it-works sheet,
    // cart lines) — checked BEFORE the /policies/* gate below, because the
    // subscription policy lives at a /pages/ URL that gate would reject.
    var sub = e.target.closest('.mm-subpolicy');
    if (sub) {
      e.preventDefault();
      e.stopPropagation();
      open(sub.getAttribute('href') || DEFAULT_URL, 'Subscription policy');
      return;
    }

    // Any ordinary link to a Shopify policy opens here too — including ones
    // dropped into a richtext field with the editor's own link picker. That
    // means an FAQ answer or buybox accordion needs no special markup: pick
    // "Refund policy" in the link dialog and it reads in place. Scoped to
    // /policies/* so it can't swallow normal page links.
    var link = e.target.closest('a[href]');
    if (!link) return;
    var href = link.getAttribute('href') || '';
    // Same-origin only: an absolute URL to another shop's policy is a real
    // navigation, not something to inline.
    if (link.host && link.host !== window.location.host) return;
    if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?policies\//i.test(href)) return;
    e.preventDefault();
    e.stopPropagation();
    // No title passed: the heading comes from the policy page's own <h1>
    // ("Refund policy"), not the link's wording ("View our Return Policy
    // Here"), which would read badly as a modal heading.
    open(href, '');
  }, true);

  modal.querySelectorAll('[data-spm-close]').forEach(function (el) {
    el.addEventListener('click', function (e) { e.preventDefault(); close(); });
  });
  // Capture phase so this runs before the bubble-phase Escape handlers in
  // pdp-trimmer.js / pdp-mm15.js, and stopPropagation so ONE Escape closes
  // only the reader (the topmost layer), not the cart drawer beneath it too.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || modal.hidden) return;
    e.stopPropagation();
    close();
  }, true);
})();
